/*
 * Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this
 * software and associated documentation files (the "Software"), to deal in the Software
 * without restriction, including without limitation the rights to use, copy, modify,
 * merge, publish, distribute, sublicense, and/or sell copies of the Software, and to
 * permit persons to whom the Software is furnished to do so.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { firstValueFrom, Observable, throwError } from 'rxjs';
import { catchError, defaultIfEmpty, map, tap } from 'rxjs/operators';
import { LocalStorageStoreService } from '../services/local-storage-store.service';

@Injectable({
  providedIn: 'root'
})
export class AuthConfigurationService {
  params$!: Observable<ConfigParams>;
  params!: ConfigParams;
  tenantName!: string;

  get appSyncUrl() {
    return this.localStorageStore.appSyncUrl.get();
  }


  constructor(
    private http: HttpClient,
    private localStorageStore: LocalStorageStoreService) {

  }

  public ConfigureDummy() {
    return () => Promise.resolve(true);
  }


  public Configure() {
    return () => Promise.resolve(true);
  }

  public LogOutOfCognito(): Observable<any> {
    let appClientId = this.localStorageStore.appClientId.get();
    let userPoolId = this.localStorageStore.userPoolId.get();
    let region = userPoolId.split('_')[0];

    const stsServer = `https://cognito-idp.${region}.amazonaws.com/` + userPoolId;
    const clientId = appClientId;
    const logoutUrl = `${window.location.origin}/`;

    return this.http.get<OpenIdConfigParams>(stsServer + "/.well-known/openid-configuration").pipe(
      tap((openIdConfigParams) => {
        const url = openIdConfigParams.authorization_endpoint.replace("oauth2/authorize", "");
        window.location.href = url + `logout?client_id=${clientId}&logout_uri=${logoutUrl}`;
      })
    )
  }

  public setTenantConfig(tenantName: string): Promise<any> {
    const url = `${environment.regApiGatewayUrl}/tenant/init/` + tenantName;
    this.params$ = this.http.get<ConfigParams>(url);
    
    const setup$ = this.params$.pipe(
      map((val) => {
        this.localStorageStore.appSyncUrl.set(val.apiUrl);
        this.localStorageStore.userPoolId.set(val.userPoolId);
        this.localStorageStore.appClientId.set(val.appClientId);

        this.setOidcConfig();
        return "success";

      }),
      catchError((error) =>  throwError(() => error))
    );


    return firstValueFrom(setup$.pipe(defaultIfEmpty('success')));

  }

  public setOidcConfig() {
    // OidcConfigService is no longer available in v15
    // config is loaded via StsConfigLoader
    // keeping method for compatibility with exisitng call sites
    return;
  }
}

export interface ConfigParams {
  //clearHashAfterLogin: boolean;
  issuer?: string;
  appClientId: string;
  userPoolId: string;
  clientId?: string;
  // nonceStateSeparator: string;
  redirectUri?: string;
  responseType?: string;
  scope?: string;
  sessionChecksEnabled?: boolean;
  // showDebugInformation: boolean;
  silentRefreshRedirectUri?: string;
  // silentRefreshTimeout: number;
  start_checksession?: boolean;
  // strictDiscoveryDocumentValidation: boolean;
  // timeoutFactor: number;
  // useSilentRefresh: boolean;
  cognitoDomain?: string;
  //apiGatewayUrl?: string;
  apiUrl: string;
}

export interface OpenIdConfigParams {
  authorization_endpoint: string;
}
